import type { i18n as I18n, PostProcessorModule } from "i18next";
import config from "@/config";
import { log } from "@/config/logging";
import {
  getPostProcessorResolvedLocale,
  getPostProcessorResolvedNamespace,
} from "./post-processor-options";

const reportedMisses = new Set<string>();
const reportedLoadFailures = new Set<string>();

function toLocales(lngs: readonly string[] | string): readonly string[] {
  return typeof lngs === "string" ? [lngs] : lngs;
}

/**
 * i18next `missingKeyHandler` — requires `saveMissing: true`. Fires once per
 * `t()` call that resolves to a fallback, so dedupe per locale/namespace/key
 * for the session rather than logging on every render.
 */
export function handleMissingKey(
  lngs: readonly string[] | string,
  ns: string,
  key: string
) {
  if (!config.translations.cdnEnabled) return;

  for (const locale of toLocales(lngs)) {
    const dedupeKey = `${locale}|${ns}|${key}`;
    if (reportedMisses.has(dedupeKey)) continue;
    reportedMisses.add(dedupeKey);

    log.warn("Missing translation key", {
      attributes: {
        missingKey: key,
        namespace: ns,
        locale,
        pageRoute: window.location.pathname,
        failureCategory: "translation_miss",
      },
    });
  }
}

/**
 * i18next `failedLoading` event — fires when a locale/namespace resource
 * (e.g. a CDN fetch) fails, distinct from an individual key being missing.
 */
export function handleFailedLoading(lng: string, ns: string, message: string) {
  const dedupeKey = `${lng}|${ns}`;
  if (reportedLoadFailures.has(dedupeKey)) return;
  reportedLoadFailures.add(dedupeKey);

  log.warn("Translation resource failed to load", {
    attributes: {
      locale: lng,
      namespace: ns,
      pageRoute: window.location.pathname,
      failureCategory: "cdn_load_failure",
      underlyingMessage: message,
    },
  });
}

/**
 * Registers as a global `postProcess` (with `postProcessPassResolved: true`)
 * so every `t()` call across the app is checked, with no wrapper or per
 * call-site change. `usedLng` tells us which language actually satisfied a
 * given call — if it differs from the active locale, that call fell back to
 * English right now, for a key a real user just tried to render.
 */
export function createMissingKeyPostProcessor(
  instance: I18n
): PostProcessorModule {
  return {
    type: "postProcessor",
    name: "missing-key-reporter",
    process(value, key, options) {
      const usedLng = getPostProcessorResolvedLocale(options);
      const activeLng = instance.language;
      if (usedLng === undefined || usedLng === activeLng) {
        return value;
      }

      const ns = getPostProcessorResolvedNamespace(options) ?? "translation";
      for (const k of Array.isArray(key) ? key : [key]) {
        handleMissingKey(activeLng, ns, k);
      }
      return value;
    },
  };
}
